import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CornerDownRight, FileWarning, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchRedirects,
  createRedirect,
  deleteRedirect,
  SEO_STR as S,
  type RedirectRow,
} from './seoApi'

/**
 * A12.11 — Редиректы (301/302). Full CRUD against the LIVE backend
 * (`/admin/seo/redirects`): inline add-form + delete row-action, DataTable
 * refetches via reloadKey after each mutation. Placeholder permission USERS_VIEW.
 */
export function RedirectsScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [code, setCode] = useState<301 | 302>(301)
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!from.trim() || !to.trim()) return
    setBusy(true)
    try {
      await createRedirect({ from_path: from.trim(), to_path: to.trim(), code })
      toast(S.created2, { tone: 'success' })
      setFrom('')
      setTo('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<RedirectRow>[]>(
    () => [
      { key: 'from_path', header: S.from, type: 'text' },
      { key: 'to_path', header: S.to, type: 'text' },
      { key: 'code', header: S.code, type: 'number' },
      { key: 'hits', header: S.hits, type: 'number' },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<RedirectRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.delete,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await deleteRedirect(r.id)
            toast(S.deleted, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchRedirects, [])
  return (
    <>
      <PageHeader
        title={S.redirects}
        screenId="A12.11"
        actions={
          <Link
            to="/seo/not-found"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <FileWarning size={13} />
            {S.notFound}
          </Link>
        }
      />
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        <input
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          placeholder="/old-path"
          spellCheck={false}
          className="min-w-[180px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 font-mono text-sm text-primary outline-none focus:border-border-strong"
        />
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="/new-path"
          spellCheck={false}
          className="min-w-[180px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 font-mono text-sm text-primary outline-none focus:border-border-strong"
        />
        <select
          value={code}
          onChange={(e) => setCode(Number(e.target.value) as 301 | 302)}
          className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none"
        >
          <option value={301}>301</option>
          <option value={302}>302</option>
        </select>
        <button
          type="button"
          onClick={add}
          disabled={busy || !from.trim() || !to.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<RedirectRow>
        key={reloadKey}
        screenId="A12.11"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <CornerDownRight size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
